import { useId } from 'react';
import { cn } from '../lib/cn.js';

/**
 * Marque OstéoPro.
 *
 * Reprise de la grammaire du logo PhysioPro, demandée telle quelle : une lettre coupée en deux
 * par une gouttière verticale, moitié gauche cyan et moitié droite rouge, aplats francs sans
 * dégradé, et une entaille. Là où PhysioPro découpe un P, on découpe un O.
 *
 * Les deux teintes sont **relevées au pixel** sur `physiopro-logo.jpg` : `#41D2FF` et `#FF1C51`.
 * Elles ne passent volontairement pas par les jetons sémantiques du thème — ce sont les couleurs
 * d'une marque, pas des couleurs d'interface. Un logo qui change de teinte avec le thème n'est plus
 * un logo. C'est la seule exception à la règle « aucune couleur brute », et elle s'arrête ici.
 *
 * La contre-forme est **peinte en blanc**, comme celle du P d'origine, et non laissée évidée. Sur
 * fond clair, rien ne la distingue de la page ; sur fond sombre, elle devient le point blanc au
 * cœur de la marque, et c'est l'effet recherché.
 *
 * Elle ne **touche pas** la lettre, exactement comme sur le P : le disque est en retrait de 3,5,
 * soit la largeur de la gouttière, ce qui laisse tout autour un liseré transparent franc. C'est ce
 * qui empêche le blanc de se souder au tracé, et c'est le même écart que celui qui sépare les deux
 * moitiés — la marque n'a qu'une seule valeur de respiration.
 *
 * Géométrie : anneau centré en (50, 50), rayon extérieur 38, rayon intérieur 19, gouttière de
 * 3,5 centrée sur l'axe. Les points de coupe sont les intersections exactes des deux cercles avec
 * les droites x = 48,25 et x = 51,75.
 *
 * L'entaille est au milieu de l'arc rouge : l'arc va de −87,4° à +87,4° autour du centre, sa
 * bissectrice est donc l'horizontale y = 50, côté droit, et l'entaille suit ce rayon. Épaisseur
 * 4,5, centrée sur y = 50.
 */

const CYAN = '#41D2FF';
const RED = '#FF1C51';
/** Contre-forme. Blanche comme sur l'original, et non transparente. */
const COUNTER = '#FFFFFF';
/** Rayon intérieur de l'anneau moins la gouttière entière : le blanc s'arrête bien avant le tracé. */
const COUNTER_RADIUS = 15.5;

/** Moitié gauche de l'anneau : extérieur vers le bas par la gauche, intérieur vers le haut. */
const LEFT_HALF = 'M48.25 12.04 A38 38 0 0 0 48.25 87.96 L48.25 68.92 A19 19 0 0 1 48.25 31.08 Z';

/** Moitié droite, symétrique. */
const RIGHT_HALF = 'M51.75 12.04 A38 38 0 0 1 51.75 87.96 L51.75 68.92 A19 19 0 0 0 51.75 31.08 Z';

export interface LogoProps extends React.ComponentProps<'svg'> {
  /** Titre accessible. Absent, la marque est décorative et sort de l'arbre d'accessibilité. */
  title?: string;
}

export function Logo({ className, title, ...props }: LogoProps) {
  // `useId` : deux marques sur une même page ne doivent pas se disputer l'identifiant du masque.
  const maskId = `logo-cut-${useId()}`;

  return (
    <svg
      viewBox="0 0 100 100"
      className={cn('h-9 w-9', className)}
      role={title === undefined ? 'presentation' : 'img'}
      aria-hidden={title === undefined ? true : undefined}
      aria-label={title}
      {...props}
    >
      <mask id={maskId}>
        <rect width="100" height="100" fill="#fff" />
        <rect x="50" y="47.75" width="50" height="4.5" fill="#000" />
      </mask>
      {/* Posée en premier, en retrait : elle transparaît par le creux sans jamais toucher l'anneau. */}
      <circle cx="50" cy="50" r={COUNTER_RADIUS} fill={COUNTER} />
      <path d={LEFT_HALF} fill={CYAN} />
      <path d={RIGHT_HALF} fill={RED} mask={`url(#${maskId})`} />
    </svg>
  );
}
